import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useDebouncedValue } from "../utils/fetchDebounce.js";
import { cachedGet } from "../utils/apiCache.js";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ComposedChart,
  Bar,
} from "recharts";
import { apiPath } from "../apiurl.js";
import LoadingSequence from "../assets/Loading/LoadingSequence";
import "../assets/css/Linechart.css";

// ─── Metadata multi-select dropdown ──────────────────────────────────────────
function MetaMultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const toggle   = (val) => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);

  return (
    <div className="lc-mms-wrap" ref={ref}>
      <button type="button" className={`lc-mms-trigger ${selected.length > 0 ? 'active' : ''}`} onClick={() => setOpen(v => !v)}>
        {selected.length === 0
          ? <span className="lc-mms-placeholder">Any {label}</span>
          : <span className="lc-mms-value">{selected.length === 1 ? selected[0] : `${selected.length} ${label}`}</span>
        }
        <span className="lc-mms-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="lc-mms-dropdown">
          <input className="lc-mms-search" placeholder={`Search ${label}…`} value={search} onChange={e => setSearch(e.target.value)} autoFocus />
          <div className="lc-mms-list">
            {filtered.length === 0 && <div className="lc-mms-empty">No matches</div>}
            {filtered.map(opt => (
              <label key={opt} className="lc-mms-option">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                <span title={opt}>{opt}</span>
              </label>
            ))}
          </div>
          {selected.length > 0 && (
            <button type="button" className="lc-mms-clear" onClick={() => { onChange([]); setSearch(''); }}>Clear {label}</button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Chart palette ────────────────────────────────────────────────────────────
const C = {
  accent:   "#4f46e5",
  accent2:  "#7c3aed",
  teal:     "#0891b2",
  grid:     "#e2e8f0",
  axisText: "#94a3b8",
};

const COMPARE_COLORS = ["#f59e0b", "#10b981", "#ef4444", "#ec4899", "#14b8a6", "#f97316"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Returns { label, year, month (1-based) } for N months ago
function getMonthMeta(monthsAgo) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - monthsAgo);
  return {
    label: d.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    year:  d.getFullYear(),
    month: d.getMonth() + 1, // 1-based
  };
}

// Build the dropdown options: last 6 calendar months
function buildMonthOptions() {
  const opts = [];
  for (let m = 1; m <= 6; m++) {
    opts.push({ monthsAgo: m, ...getMonthMeta(m) });
  }
  return opts;
}

const MONTH_OPTIONS = buildMonthOptions();

// ─── Column definitions ───────────────────────────────────────────────────────
const COLUMNS = [
  { key: "id",               label: "ID",            sortable: true,  align: "left"  },
  { key: "Year",             label: "Year",           sortable: true,  align: "left"  },
  { key: "Period",           label: "Period",         sortable: false, align: "left"  },
  { key: "TotalOccurrences", label: "Processed Runs", sortable: true,  align: "right" },
  { key: "C",                label: "Completed",      sortable: true,  align: "right" },
  { key: "R",                label: "Ready",          sortable: true,  align: "right" },
  { key: "F",                label: "Failed",         sortable: true,  align: "right" },
  { key: "TotalSizeBytes",   label: "Total (Bytes)",  sortable: true,  align: "right" },
  { key: "TotalSizeKB",      label: "Total (KB)",     sortable: true,  align: "right" },
  { key: "TotalSizeMB",      label: "Total (MB)",     sortable: true,  align: "right" },
  { key: "TotalSizeGB",      label: "Total (GB)",     sortable: true,  align: "right" },
  { key: "TotalSizeTB",      label: "Total (TB)",     sortable: true,  align: "right" },
  { key: "AverageSizeGB",    label: "Avg Size (GB)",  sortable: true,  align: "right" },
];

const PAGE_SIZES = [5, 10, 25, 50];

function buildPageButtons(totalPages, page) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
  const btns = [];
  if (page > 2) { btns.push(0); if (page > 3) btns.push("…"); }
  for (let i = Math.max(0, page - 2); i <= Math.min(totalPages - 1, page + 2); i++) btns.push(i);
  if (page < totalPages - 3) { if (page < totalPages - 4) btns.push("…"); btns.push(totalPages - 1); }
  return btns;
}

// ─── Custom tooltip ───────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const filtered = payload.filter((e) => e?.name?.trim());
  return (
    <div className="lc-tooltip">
      <div className="lc-tooltip-label">{label}</div>
      {filtered.map((entry, i) => (
        <div className="lc-tooltip-row" key={`${entry.dataKey}-${i}`}>
          <span className="lc-tooltip-key">
            <span className="lc-tooltip-dot" style={{ background: entry.color }} />
            {entry.name}
          </span>
          <span className="lc-tooltip-val">
            {typeof entry.value === "number" ? entry.value.toLocaleString() : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Shared recharts props ────────────────────────────────────────────────────
const getXAxisProps = (b) => {
  const dense = b === "day" || b === "hour";
  return {
    tick:       { fill: C.axisText, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" },
    axisLine:   { stroke: C.grid },
    tickLine:   { stroke: C.grid },
    tickMargin: dense ? 8 : 12,
    height:     dense ? 64 : 48,
    angle:      dense ? -35 : 0,
    textAnchor: dense ? "end" : "middle",
  };
};

const yAxisProps = (color) => ({
  tick:     { fill: color || C.axisText, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" },
  axisLine: { stroke: C.grid },
  tickLine: { stroke: C.grid },
  width:    65,
});

const commonMargin = { top: 16, right: 24, bottom: 56, left: 8 };
const gridProps    = { strokeDasharray: "4 4", stroke: C.grid, vertical: false };

const yLabel = (value, angle, position, fill) => ({
  value, angle, position,
  fill: fill || C.axisText,
  fontSize: 11,
  fontFamily: "'JetBrains Mono', monospace",
  offset: 10,
});

// ─── Data table ───────────────────────────────────────────────────────────────
function DataTable({ rows, loading, rawTotalRuns, rawTotalGB }) {
  const [sortKey,  setSortKey]  = useState("id");
  const [sortDir,  setSortDir]  = useState("asc");
  const [page,     setPage]     = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const handleSort = (col) => {
    if (!col.sortable) return;
    if (sortKey === col.key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(col.key); setSortDir("asc"); }
    setPage(0);
  };

  const thClass = (col) => {
    if (!col.sortable) return "";
    return sortKey === col.key ? `sortable sort-${sortDir}` : "sortable";
  };

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const r = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === "asc" ? r : -r;
  }), [rows, sortKey, sortDir]);

  const totalPages  = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage    = Math.min(page, totalPages - 1);
  const paged       = sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);
  const pageButtons = buildPageButtons(totalPages, safePage);
  const fmt = (v, key) =>
    v == null ? "—" : key === "Year" ? String(v) : typeof v === "number" ? v.toLocaleString() : v;

  const totalRuns = rawTotalRuns !== undefined ? rawTotalRuns : rows.reduce((s, r) => s + (r.TotalOccurrences || 0), 0);
  const totalGB   = rawTotalGB  !== undefined ? rawTotalGB  : rows.reduce((s, r) => s + (r.TotalSizeGB || 0), 0);

  if (loading) return (
    <div className="lc-loading"><div className="lc-spinner" /><span>Loading data…</span></div>
  );

  return (
    <div>
      <div className="lc-statsbar">
        <div className="lc-stat-chip">
          <span className="lc-stat-label">Periods :</span>
          <span className="lc-stat-val">{rows.length.toLocaleString()}</span>
        </div>
        <div className="lc-stat-chip">
          <span className="lc-stat-label">Total Runs :</span>
          <span className="lc-stat-val">{totalRuns.toLocaleString()}</span>
        </div>
        <div className="lc-stat-chip">
          <span className="lc-stat-label">Total Size :</span>
          <span className="lc-stat-val">
            {totalGB.toLocaleString(undefined, { maximumFractionDigits: 2 })} GB
          </span>
        </div>
        <div className="lc-stat-chip">
          <span className="lc-stat-label">Rows/page</span>
          <select
            className="lc-page-size-select"
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
          >
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="lc-table-wrap">
        {paged.length === 0 ? (
          <div className="lc-empty">
            <span style={{ fontSize: 18, opacity: 0.4 }}>◎</span>
            <span>No data available</span>
          </div>
        ) : (
          <table className="lc-table">
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th key={col.key} className={thClass(col)} onClick={() => handleSort(col)}
                    style={{ textAlign: col.align, cursor: col.sortable ? "pointer" : "default" }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.map((row) => (
                <tr key={row.id}>
                  {COLUMNS.map((col) => (
                    <td key={col.key} style={{ textAlign: col.align }}>
                      <span className="mono-val">{fmt(row[col.key], col.key)}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {rows.length > 0 && (
          <div className="lc-pagination">
            <span>
              Showing {(safePage * pageSize + 1).toLocaleString()}–
              {Math.min((safePage + 1) * pageSize, sorted.length).toLocaleString()} of{" "}
              {sorted.length.toLocaleString()} rows
            </span>
            <div className="lc-page-btns">
              <button className="lc-page-btn" disabled={safePage === 0} onClick={() => setPage(0)}>«</button>
              <button className="lc-page-btn" disabled={safePage === 0} onClick={() => setPage((p) => p - 1)}>‹</button>
              {pageButtons.map((b, i) =>
                b === "…"
                  ? <span className="lc-page-ellipsis" key={`e${i}`}>…</span>
                  : <button key={b} className={`lc-page-btn ${b === safePage ? "current" : ""}`} onClick={() => setPage(b)}>{b + 1}</button>
              )}
              <button className="lc-page-btn" disabled={safePage >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>›</button>
              <button className="lc-page-btn" disabled={safePage >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const TABS = [
  { key: "combined", label: "Combined View"  },
  { key: "runs",     label: "Processed Runs" },
  { key: "status",   label: "Run Status"     },
  { key: "size",     label: "Total Size"     },
  { key: "average",  label: "Avg Size / Run" },
];

const TIME_BTNS = [
  { key: "all",                 label: "All Time"      },
  { key: "1y",                  label: "1 Year"        },
  { key: "6m",                  label: "6 Months"      },
  { key: "currentMonth",        label: "Current Month" },
  { key: "lastTwentyFourHours", label: "Last 24 h"     },
];

function Linechart() {
  const [data,        setData]        = useState([]);
  const [gridData,    setGridData]    = useState([]);
  const [rawTotals,   setRawTotals]   = useState({ totalRuns: 0, totalGB: 0 });
  const [loading,     setLoading]     = useState(true);
  const [activeTab,   setActiveTab]   = useState("combined");
  const [timeRange,   setTimeRange]   = useState("currentMonth");
  const debouncedTimeRange = useDebouncedValue(timeRange, 300);

  // Metadata filters
  const [metaFilters, setMetaFilters] = useState({ name: [], organism: [], platform: [], country: [], center: [], continent: [], isoSource: [] });
  const [metaOptions, setMetaOptions] = useState({ names: [], organisms: [], platforms: [], countries: [], centers: [], continents: [], isolationSources: [] });

  // Selected comparison month indices (monthsAgo values), e.g. [1, 3]
  const [selectedMonths, setSelectedMonths] = useState([]);

  // Map of monthsAgo → fetched day-by-day series data
  const [compData,    setCompData]    = useState({});
  const [compLoading, setCompLoading] = useState({});

  // ── Cadence state (from cadence-timeline endpoint) ───────────────────────
  const [cadenceByKey,   setCadenceByKey]   = useState({});
  const [cadenceTotals,  setCadenceTotals]  = useState({ C: 0, R: 0, F: 0 });
  const [cadenceLoading, setCadenceLoading] = useState(false);

  // ── 6-month drill-down ────────────────────────────────────────────────────
  // null = show all 6 months aggregated by month
  // "YYYY-M" = drill into that month's day-by-day data
  const [drillMonth,    setDrillMonth]    = useState(null);
  const [drillData,     setDrillData]     = useState([]);
  const [drillGridData, setDrillGridData] = useState([]);
  const [drillLoading,  setDrillLoading]  = useState(false);

  // ── Date helpers ──────────────────────────────────────────────────────────
  const getMonthNumber = (v) => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string") {
      const n = Number.parseInt(v.trim(), 10);
      if (!Number.isNaN(n)) return n;
      const map = {
        jan:1,january:1,feb:2,february:2,mar:3,march:3,apr:4,april:4,may:5,
        jun:6,june:6,jul:7,july:7,aug:8,august:8,sep:9,sept:9,september:9,
        oct:10,october:10,nov:11,november:11,dec:12,december:12,
      };
      return map[v.trim().toLowerCase()];
    }
    return undefined;
  };

  const getBucketForTimeRange = (r) =>
    ({ lastTwentyFourHours:"hour", currentMonth:"day", "6m":"month", "1y":"month", all:"year" }[r] || "year");

  const bucket = useMemo(() => getBucketForTimeRange(timeRange), [timeRange]);

  const getPeriodStart = (item, b = bucket) => {
    const year = Number.parseInt(item?.Year, 10); if (Number.isNaN(year)) return null;
    const mo   = getMonthNumber(item?.Month) || 1;
    const day  = Number.parseInt(item?.Day,  10) || 1;
    if (b === "hour")  { const h = Number.parseInt(item?.Hour, 10) || 0; return new Date(year, mo-1, day, h, 0, 0, 0); }
    if (b === "day")   return new Date(year, mo-1, day, 0, 0, 0, 0);
    if (b === "month") return new Date(year, mo-1, 1,   0, 0, 0, 0);
    return new Date(year, 0, 1, 0, 0, 0, 0);
  };

  const formatDateLabel = (item, b = bucket) => {
    const year = Number.parseInt(item?.Year, 10); if (Number.isNaN(year)) return "Unknown";
    const mo   = getMonthNumber(item?.Month) || 1;
    const day  = Number.parseInt(item?.Day,  10) || 1;
    const h    = Number.parseInt(item?.Hour, 10) || 0;
    if (b === "hour")  return new Date(year, mo-1, day, h).toLocaleTimeString("en-US", { hour:"numeric", hour12:true });
    if (b === "day")   return new Date(year, mo-1, day).toLocaleDateString("en-US", { month:"short", day:"numeric" });
    if (b === "month") return new Date(year, mo-1, 1).toLocaleDateString("en-US", { month:"short", year:"numeric" });
    return String(year);
  };

  const getBucketKey = (item, b = bucket) => {
    const year = Number.parseInt(item?.Year, 10); if (Number.isNaN(year)) return null;
    const mo   = getMonthNumber(item?.Month) || 1;
    const day  = Number.parseInt(item?.Day,  10) || 1;
    const h    = Number.parseInt(item?.Hour, 10) || 0;
    if (b === "hour")  return `${year}-${mo}-${day}-${h}`;
    if (b === "day")   return `${year}-${mo}-${day}`;
    if (b === "month") return `${year}-${mo}`;
    return `${year}`;
  };

  const aggregateItems = (items, b) => {
    const agg = {};
    items.forEach((item) => {
      if (item?.Year == null) return;
      const norm = {
        Year:             Number.parseInt(item.Year, 10),
        Month:            getMonthNumber(item.Month) || 1,
        Day:              Number.parseInt(item.Day,  10) || 1,
        Hour:             Number.parseInt(item.Hour, 10) || 0,
        TotalOccurrences: Number(item.TotalOccurrences) || 0,
        Completed:        Number(item.Completed)        || 0,
        Failed:           Number(item.Failed)           || 0,
        TotalSizeBytes:   Number(item.TotalSizeBytes)   || 0,
        TotalSizeKB:      Number(item.TotalSizeKB)      || 0,
        TotalSizeMB:      Number(item.TotalSizeMB)      || 0,
        TotalSizeGB:      Number(item.TotalSizeGB)      || 0,
        TotalSizeTB:      Number(item.TotalSizeTB)      || 0,
      };
      const key = getBucketKey(norm, b);
      if (!key) return;
      if (!agg[key]) { agg[key] = { ...norm }; return; }
      agg[key].TotalOccurrences += norm.TotalOccurrences;
      agg[key].Completed        += norm.Completed;
      agg[key].Failed           += norm.Failed;
      agg[key].TotalSizeBytes   += norm.TotalSizeBytes;
      agg[key].TotalSizeKB      += norm.TotalSizeKB;
      agg[key].TotalSizeMB      += norm.TotalSizeMB;
      agg[key].TotalSizeGB      += norm.TotalSizeGB;
      agg[key].TotalSizeTB      += norm.TotalSizeTB;
    });
    return Object.values(agg).sort((a, b2) =>
      (getPeriodStart(a, b)?.getTime() || 0) - (getPeriodStart(b2, b)?.getTime() || 0)
    );
  };

  // ── Fetch metadata options for dropdowns ─────────────────────────────────
  useEffect(() => {
    axios.get(`${apiPath}/api/dashboard-status/metadata-counts`).then(res => {
      const d = res.data;
      setMetaOptions({
        names:            (d.names            || []).map(x => x.label),
        organisms:        (d.organisms        || []).map(x => x.label),
        platforms:        (d.platforms        || []).map(x => x.label),
        countries:        (d.countries        || []).map(x => x.label),
        centers:          (d.centerNames      || []).map(x => x.label),
        continents:       (d.continents       || []).map(x => x.label),
        isolationSources: (d.isolationSources || []).map(x => x.label),
      });
    }).catch(() => {});
  }, []);

  // ── Build meta query string ───────────────────────────────────────────────
  const metaQS = useMemo(() => {
    const p = new URLSearchParams();
    if (metaFilters.name.length      > 0) p.set('metaName',      metaFilters.name.join(','));
    if (metaFilters.organism.length  > 0) p.set('metaOrganism',  metaFilters.organism.join(','));
    if (metaFilters.platform.length  > 0) p.set('metaPlatform',  metaFilters.platform.join(','));
    if (metaFilters.country.length   > 0) p.set('metaCountry',   metaFilters.country.join(','));
    if (metaFilters.center.length    > 0) p.set('metaCenter',    metaFilters.center.join(','));
    if (metaFilters.continent.length > 0) p.set('metaContinent', metaFilters.continent.join(','));
    if (metaFilters.isoSource.length > 0) p.set('metaIsoSource', metaFilters.isoSource.join(','));
    const s = p.toString();
    return s ? `&${s}` : '';
  }, [metaFilters]);

  const activeMetaCount = Object.values(metaFilters).reduce((n, arr) => n + arr.length, 0);

  // ── Main fetch ────────────────────────────────────────────────────────────
  // bucket is intentionally NOT in the dep array: it's derived from timeRange
  // (instant), while debouncedTimeRange is delayed 300ms. Including bucket
  // caused a spurious fetch with the wrong timeRange/bucket combination on
  // every time-range switch — the stale currentMonth data would appear instead
  // of the newly selected range's data.
  useEffect(() => {
    const b = getBucketForTimeRange(debouncedTimeRange); // always consistent with the URL
    setLoading(true);
    setSelectedMonths([]);
    setCompData({});
    setDrillMonth(null);
    setData([]);
    setGridData([]);
    setDrillData([]);
    setDrillGridData([]);
    cachedGet(axios, `${apiPath}/api/datasize?timeRange=${debouncedTimeRange}${metaQS}`, {
      ttl: 5 * 60 * 1000,
      swr: 10 * 60 * 1000,
    })
      .then((resData) => {
        const baseData = Array.isArray(resData) ? resData : [];
        setRawTotals({
          totalRuns: baseData.reduce((s, r) => s + (Number(r.TotalOccurrences) || 0), 0),
          totalGB:   baseData.reduce((s, r) => s + (Number(r.TotalSizeGB)      || 0), 0),
        });
        const sorted = aggregateItems(baseData, b);

        setData(
          sorted.map((item) => ({
            date:          formatDateLabel(item, b),
            periodStart:   getPeriodStart(item, b),
            day:           Number.parseInt(item.Day, 10) || 1,
            processedRuns: item.TotalOccurrences,
            completed:     item.Completed || 0,
            failed:        item.Failed    || 0,
            totalSizeGB:   Number((item.TotalSizeGB || 0).toFixed(2)),
            averageSizeGB: item.TotalOccurrences > 0
              ? Number(((item.TotalSizeGB || 0) / item.TotalOccurrences).toFixed(2)) : 0,
          })).filter((i) => i.periodStart)
        );

        setGridData(
          sorted.map((item) => ({
            id:               getBucketKey(item, b),
            Year:             item.Year,
            Period:           formatDateLabel(item, b),
            TotalOccurrences: item.TotalOccurrences,
            Completed:        item.Completed || 0,
            Failed:           item.Failed    || 0,
            TotalSizeBytes:   item.TotalSizeBytes,
            TotalSizeKB:      item.TotalSizeKB,
            TotalSizeMB:      item.TotalSizeMB,
            TotalSizeGB:      Number(item.TotalSizeGB.toFixed(2)),
            TotalSizeTB:      Number(item.TotalSizeTB.toFixed(2)),
            AverageSizeGB:    item.TotalOccurrences > 0
              ? Number((item.TotalSizeGB / item.TotalOccurrences).toFixed(2)) : 0,
          }))
        );
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching datasize:", err);
        setData([]);
        setGridData([]);
        setRawTotals({ totalRuns: 0, totalGB: 0 });
        setLoading(false);
      });
  }, [debouncedTimeRange, metaQS]); // bucket intentionally omitted — see comment above

  // ── Cadence fetch (parallel to main fetch) ───────────────────────────────
  // cadence-timeline → chart bars; 3 separate cadence-counts calls → totals strip
  useEffect(() => {
    setCadenceLoading(true);
    const b  = getBucketForTimeRange(debouncedTimeRange);
    const tr = debouncedTimeRange;
    const opts = { ttl: 5 * 60 * 1000, swr: 10 * 60 * 1000 };
    Promise.all([
      cachedGet(axios, `${apiPath}/api/dashboard-status/cadence-timeline?timeRange=${tr}`, opts),
      cachedGet(axios, `${apiPath}/api/dashboard-status/cadence-counts?timeRange=${tr}&state=C`, opts),
      cachedGet(axios, `${apiPath}/api/dashboard-status/cadence-counts?timeRange=${tr}&state=R`, opts),
      cachedGet(axios, `${apiPath}/api/dashboard-status/cadence-counts?timeRange=${tr}&state=F`, opts),
    ])
      .then(([timelineRaw, cRaw, rRaw, fRaw]) => {
        // Build chart data from timeline
        const arr = Array.isArray(timelineRaw) ? timelineRaw : [];
        const byKey = {};
        const validStates = new Set(['C', 'R', 'F']);
        arr.forEach((item) => {
          if (!item.state || !validStates.has(item.state)) return;
          const key = getBucketKey(item, b);
          if (!key) return;
          if (!byKey[key]) byKey[key] = { _key: key, date: formatDateLabel(item, b), C: 0, R: 0, F: 0 };
          byKey[key][item.state] += item.count;
        });
        setCadenceByKey(byKey);

        // Totals from 3 dedicated cadence-counts calls
        const getCount = (raw, state) => {
          const list = Array.isArray(raw) ? raw : [];
          const found = list.find((r) => r.state === state);
          return found ? found.count : 0;
        };
        setCadenceTotals({
          C: getCount(cRaw, 'C'),
          R: getCount(rRaw, 'R'),
          F: getCount(fRaw, 'F'),
        });
        setCadenceLoading(false);
      })
      .catch(() => {
        setCadenceByKey({});
        setCadenceTotals({ C: 0, R: 0, F: 0 });
        setCadenceLoading(false);
      });
  }, [debouncedTimeRange]);

  // ── Toggle a comparison month on/off ─────────────────────────────────────
  const toggleMonth = (monthsAgo) => {
    setSelectedMonths((prev) => {
      const next = prev.includes(monthsAgo)
        ? prev.filter((m) => m !== monthsAgo)   // deselect
        : [...prev, monthsAgo];                  // select

      // If newly selected, fetch its data
      if (!prev.includes(monthsAgo)) {
        fetchMonthData(monthsAgo);
      }

      return next;
    });
  };

  // ── Fetch a specific previous month from the API ──────────────────────────
  // Uses prevMonth:YYYY-M so the backend returns only that month's data
  const fetchMonthData = (monthsAgo) => {
    const { year, month, label } = getMonthMeta(monthsAgo);
    const timeRangeParam = `prevMonth:${year}-${month}`;

    setCompLoading((prev) => ({ ...prev, [monthsAgo]: true }));

    axios.get(`${apiPath}/api/datasize?timeRange=${timeRangeParam}${metaQS}`)
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : [];

        // Aggregate day-by-day — use ACTUAL day number from the data
        const dayAgg = {};
        raw.forEach((item) => {
          const day  = Number.parseInt(item?.Day, 10);
          if (isNaN(day)) return;
          const runs  = Number(item.TotalOccurrences) || 0;
          const gb    = Number(item.TotalSizeGB)      || 0;
          const comp  = Number(item.Completed)        || 0;
          const failed = Number(item.Failed)            || 0;
          if (!dayAgg[day]) { dayAgg[day] = { day, runs, gb, completed: comp, failed }; return; }
          dayAgg[day].runs      += runs;
          dayAgg[day].gb        += gb;
          dayAgg[day].completed += comp;
          dayAgg[day].failed    += failed;
        });

        // Sort by actual day number (1–31) — this gives correct 28/29/30/31 days
        const points = Object.values(dayAgg)
          .sort((a, b) => a.day - b.day)
          .map((d) => ({
            date:          `Day ${d.day}`,
            processedRuns: d.runs,
            completed:     d.completed,
            failed:        d.failed,
            totalSizeGB:   Number(d.gb.toFixed(2)),
            averageSizeGB: d.runs > 0 ? Number((d.gb / d.runs).toFixed(2)) : 0,
          }));

        setCompData((prev) => ({ ...prev, [monthsAgo]: { label, color: COMPARE_COLORS[(monthsAgo - 1) % COMPARE_COLORS.length], points } }));
        setCompLoading((prev) => ({ ...prev, [monthsAgo]: false }));
      })
      .catch(() => {
        setCompLoading((prev) => ({ ...prev, [monthsAgo]: false }));
      });
  };

  // ── Build dropdown options from the 6m gridData (unique year-month pairs) ──
  const sixMonthOptions = useMemo(() => {
    if (timeRange !== "6m") return [];
    const seen = new Set();
    return gridData
      .map((row) => {
        const [y, m] = (row.id || "").split("-").map(Number);
        if (!y || !m) return null;
        const value = `${y}-${m}`;
        if (seen.has(value)) return null; // deduplicate
        seen.add(value);
        const label = new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
        return { value, label, year: y, month: m };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(a.year, a.month - 1) - new Date(b.year, b.month - 1));
  }, [timeRange, gridData]);

  // ── Fetch day-by-day data for the drilled month ───────────────────────────
  useEffect(() => {
    if (!drillMonth) return;
    const [y, m] = drillMonth.split("-").map(Number);
    setDrillLoading(true);

    axios.get(`${apiPath}/api/datasize?timeRange=prevMonth:${y}-${m}${metaQS}`)
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : [];
        const dayAgg = {};
        raw.forEach((item) => {
          if (item?.Year == null) return;
          const norm = {
            Year:             Number.parseInt(item.Year, 10),
            Month:            getMonthNumber(item.Month) || 1,
            Day:              Number.parseInt(item.Day,  10) || 1,
            Hour:             0,
            TotalOccurrences: Number(item.TotalOccurrences) || 0,
            Completed:        Number(item.Completed)        || 0,
            Failed:           Number(item.Failed)           || 0,
            TotalSizeBytes:   Number(item.TotalSizeBytes)   || 0,
            TotalSizeKB:      Number(item.TotalSizeKB)      || 0,
            TotalSizeMB:      Number(item.TotalSizeMB)      || 0,
            TotalSizeGB:      Number(item.TotalSizeGB)      || 0,
            TotalSizeTB:      Number(item.TotalSizeTB)      || 0,
          };
          const key = norm.Day;
          if (!dayAgg[key]) { dayAgg[key] = { ...norm }; return; }
          dayAgg[key].TotalOccurrences += norm.TotalOccurrences;
          dayAgg[key].Completed        += norm.Completed;
          dayAgg[key].Failed           += norm.Failed;
          dayAgg[key].TotalSizeBytes   += norm.TotalSizeBytes;
          dayAgg[key].TotalSizeKB      += norm.TotalSizeKB;
          dayAgg[key].TotalSizeMB      += norm.TotalSizeMB;
          dayAgg[key].TotalSizeGB      += norm.TotalSizeGB;
          dayAgg[key].TotalSizeTB      += norm.TotalSizeTB;
        });

        const sorted = Object.values(dayAgg).sort((a, b2) => a.Day - b2.Day);

        setDrillData(
          sorted.map((item) => ({
            date:          new Date(y, m - 1, item.Day).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
            periodStart:   new Date(y, m - 1, item.Day),
            day:           item.Day,
            processedRuns: item.TotalOccurrences,
            completed:     item.Completed || 0,
            failed:        item.Failed    || 0,
            totalSizeGB:   Number((item.TotalSizeGB || 0).toFixed(2)),
            averageSizeGB: item.TotalOccurrences > 0
              ? Number(((item.TotalSizeGB || 0) / item.TotalOccurrences).toFixed(2)) : 0,
          }))
        );

        setDrillGridData(
          sorted.map((item) => ({
            id:               `${y}-${m}-${item.Day}`,
            Year:             y,
            Period:           new Date(y, m - 1, item.Day).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
            TotalOccurrences: item.TotalOccurrences,
            Completed:        item.Completed || 0,
            Failed:           item.Failed    || 0,
            TotalSizeBytes:   item.TotalSizeBytes,
            TotalSizeKB:      item.TotalSizeKB,
            TotalSizeMB:      item.TotalSizeMB,
            TotalSizeGB:      Number(item.TotalSizeGB.toFixed(2)),
            TotalSizeTB:      Number(item.TotalSizeTB.toFixed(2)),
            AverageSizeGB:    item.TotalOccurrences > 0
              ? Number((item.TotalSizeGB / item.TotalOccurrences).toFixed(2)) : 0,
          }))
        );
        setDrillLoading(false);
      })
      .catch(() => setDrillLoading(false));
  }, [drillMonth, metaQS]);

  // ── Which data to display — drill overrides main when active ──────────────
  const displayData     = (timeRange === "6m" && drillMonth) ? drillData     : data;
  const displayGrid     = (timeRange === "6m" && drillMonth) ? drillGridData : gridData;
  const displayLoading  = (timeRange === "6m" && drillMonth) ? drillLoading  : loading;

  // ── Active comparison series (only selected months that have loaded) ───────
  const activeComparisons = useMemo(() =>
    selectedMonths
      .filter((m) => compData[m])
      .map((m) => ({ monthsAgo: m, ...compData[m] }))
      .sort((a, b) => a.monthsAgo - b.monthsAgo),
    [selectedMonths, compData]
  );

  const anyCompLoading = selectedMonths.some((m) => compLoading[m]);

  // ── Re-key current-month data to "Day N" when comparison is active ────────
  // Uses the ACTUAL day number stored in the data point, not the array index
  const currentData = useMemo(() => {
    if (timeRange !== "currentMonth" || selectedMonths.length === 0) return data;
    return data.map((d) => ({ ...d, date: `Day ${d.day}` }));
  }, [data, timeRange, selectedMonths]);

  // ── Merge all series into one flat recharts data array ────────────────────
  const mergedData = useMemo(() => {
    if (!activeComparisons.length) return currentData;

    // Build map keyed by "Day N"
    const map = {};
    currentData.forEach((d) => { map[d.date] = { ...d }; });

    activeComparisons.forEach((s) => {
      s.points.forEach((p) => {
        if (!map[p.date]) map[p.date] = { date: p.date };
        map[p.date][`runs_${s.monthsAgo}`] = p.processedRuns;
        map[p.date][`size_${s.monthsAgo}`] = p.totalSizeGB;
        map[p.date][`avg_${s.monthsAgo}`]  = p.averageSizeGB;
      });
    });

    // Sort numerically by day number
    return Object.values(map).sort((a, b) => {
      const na = parseInt(a.date.replace("Day ", ""), 10);
      const nb = parseInt(b.date.replace("Day ", ""), 10);
      return isNaN(na) || isNaN(nb) ? a.date.localeCompare(b.date) : na - nb;
    });
  }, [currentData, activeComparisons]);

  const aggLabel = () => {
    if (timeRange === "6m" && drillMonth) return "Day by Day";
    return ({ hour:"Hour by Hour", day:"Day by Day", month:"Month by Month" }[bucket] ?? "Year by Year");
  };

  const hasComp = activeComparisons.length > 0;

  // ── Combined table rows: current month + all selected comparison months ────
  // ── Cadence chart data (sorted chronologically by bucket key) ────────────
  const cadenceChartData = useMemo(() =>
    Object.values(cadenceByKey).sort((a, b) => a._key.localeCompare(b._key)),
  [cadenceByKey]);

  // ── Merge cadence states into grid rows by bucket key ────────────────────
  const mergedGridData = useMemo(() =>
    gridData.map((row) => ({
      ...row,
      C: cadenceByKey[row.id]?.C || 0,
      R: cadenceByKey[row.id]?.R || 0,
      F: cadenceByKey[row.id]?.F || 0,
    })),
  [gridData, cadenceByKey]);

  const combinedGrid = useMemo(() => {
    const baseGrid = (timeRange === "6m" && drillMonth) ? drillGridData : mergedGridData;
    if (timeRange !== "currentMonth" || activeComparisons.length === 0) return baseGrid;

    const now = new Date();
    const currentLabel = now.toLocaleDateString("en-US", { month: "short", year: "numeric" });

    const currentRows = baseGrid.map((row) => ({
      ...row,
      Period: `[${currentLabel}] ${row.Period}`,
    }));

    const compRows = activeComparisons.flatMap((s) =>
      (s.points || []).map((p, i) => {
        const dayNum = parseInt(p.date.replace("Day ", ""), 10);
        return {
          id:               `comp-${s.monthsAgo}-${isNaN(dayNum) ? i : dayNum}`,
          Year:             null,
          Period:           `[${s.label}] ${p.date}`,
          TotalOccurrences: p.processedRuns,
          C: 0, R: 0, F: 0,
          TotalSizeBytes:   null,
          TotalSizeKB:      null,
          TotalSizeMB:      null,
          TotalSizeGB:      p.totalSizeGB,
          TotalSizeTB:      null,
          AverageSizeGB:    p.averageSizeGB,
        };
      })
    );

    return [...currentRows, ...compRows];
  }, [mergedGridData, drillGridData, drillMonth, activeComparisons, timeRange]);

  // ── KPI summary cards ─────────────────────────────────────────────────────
  const kpiData = useMemo(() => {
    const isDrill = timeRange === "6m" && drillMonth;
    const src = displayGrid;
    const totalRuns = isDrill ? src.reduce((s, r) => s + (r.TotalOccurrences || 0), 0) : rawTotals.totalRuns;
    const totalGB   = isDrill ? src.reduce((s, r) => s + (r.TotalSizeGB      || 0), 0) : rawTotals.totalGB;
    const avgGB     = totalRuns > 0 ? totalGB / totalRuns : 0;
    return {
      periods:  src.length,
      totalRuns,
      totalGB:  Number(totalGB.toFixed(2)),
      avgGB:    Number(avgGB.toFixed(4)),
    };
  }, [displayGrid, rawTotals, timeRange, drillMonth]);

  // ── Comparison dashed lines ───────────────────────────────────────────────
  const compLines = (type) =>
    activeComparisons.map((s) => (
      <Line
        key={`${type}_${s.monthsAgo}`}
        type="monotone"
        dataKey={`${type}_${s.monthsAgo}`}
        name={s.label}
        stroke={s.color}
        strokeWidth={1.5}
        strokeDasharray="5 3"
        dot={false}
        activeDot={{ r: 4 }}
      />
    ));

  // ── Legend items for comparison months ───────────────────────────────────
  const compLegend = activeComparisons.map((s) => (
    <div className="lc-legend-item" key={s.monthsAgo}>
      <div className="lc-legend-swatch lc-legend-swatch--dashed" style={{ borderColor: s.color }} />
      {s.label}
    </div>
  ));

  // ── Chart renderer ────────────────────────────────────────────────────────
  const renderChart = () => {
    if (loading || anyCompLoading) return (
      <div className="lc-loading"><div className="lc-spinner" /><span>Loading data…</span></div>
    );

    // When drilled into a 6m month, show its day-by-day data
    const chartData    = (timeRange === "6m" && drillMonth) ? drillData : mergedData;
    const chartBarData = chartData;
    const effectiveBucket = (timeRange === "6m" && drillMonth) ? "day" : bucket;
    const xAxisProps = getXAxisProps(effectiveBucket);

    if (drillLoading) return (
      <div className="lc-loading"><div className="lc-spinner" /><span>Loading month data…</span></div>
    );

    switch (activeTab) {
      case "combined":
        return (
          <>
            <div className="lc-legend">
              <div className="lc-legend-item">
                <div className="lc-legend-swatch" style={{ background: C.accent }} />
                Processed Runs
              </div>
              <div className="lc-legend-item">
                <div className="lc-legend-swatch" style={{ background: C.accent2 }} />
                Total Size (GB)
              </div>
              {compLegend}
            </div>
            <div className="lc-chart-inner">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={commonMargin}>
                  <defs>
                    <linearGradient id="gPrimary" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.accent}  stopOpacity={0.18} />
                      <stop offset="95%" stopColor={C.accent}  stopOpacity={0.01} />
                    </linearGradient>
                    <linearGradient id="gSecondary" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.accent2} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C.accent2} stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" {...xAxisProps} />
                  <YAxis yAxisId="left"  {...yAxisProps(C.accent)}
                    label={yLabel("Processed Runs", -90, "insideLeft", C.accent)} />
                  <YAxis yAxisId="right" orientation="right" {...yAxisProps(C.accent2)}
                    label={yLabel("Size (GB)", 90, "insideRight", C.accent2)} />
                  <Tooltip content={<ChartTooltip />} cursor={{ stroke: C.grid, strokeWidth: 1 }} />
                  <Area yAxisId="left"  type="monotone" dataKey="processedRuns" name=" " fill="url(#gPrimary)"   stroke="none" legendType="none" />
                  <Area yAxisId="right" type="monotone" dataKey="totalSizeGB"   name=" " fill="url(#gSecondary)" stroke="none" legendType="none" />
                  <Line yAxisId="left"  type="monotone" dataKey="processedRuns" name="Processed Runs"  stroke={C.accent}  strokeWidth={2} dot={{ r:3, fill:C.accent,  strokeWidth:0 }} activeDot={{ r:5 }} />
                  <Line yAxisId="right" type="monotone" dataKey="totalSizeGB"   name="Total Size (GB)" stroke={C.accent2} strokeWidth={2} dot={{ r:3, fill:C.accent2, strokeWidth:0 }} activeDot={{ r:5 }} />
                  {hasComp && activeComparisons.map((s) => (
                    <React.Fragment key={s.monthsAgo}>
                      <Line yAxisId="left"  type="monotone" dataKey={`runs_${s.monthsAgo}`} name={`${s.label} — Runs`}    stroke={s.color} strokeWidth={1.5} strokeDasharray="5 3" dot={false} activeDot={{ r:4 }} />
                      <Line yAxisId="right" type="monotone" dataKey={`size_${s.monthsAgo}`} name={`${s.label} — Size GB`} stroke={s.color} strokeWidth={1.5} strokeDasharray="5 3" dot={false} activeDot={{ r:4 }} />
                    </React.Fragment>
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        );

      case "runs":
        return (
          <>
            {hasComp && (
              <div className="lc-legend">
                <div className="lc-legend-item">
                  <div className="lc-legend-swatch" style={{ background: C.accent }} />
                  Current Month
                </div>
                {compLegend}
              </div>
            )}
            <div className="lc-chart-inner">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={commonMargin}>
                  <defs>
                    <linearGradient id="gBarRuns" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={C.accent} stopOpacity={0.9} />
                      <stop offset="100%" stopColor={C.accent} stopOpacity={0.4} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" {...xAxisProps} />
                  <YAxis {...yAxisProps()} label={yLabel("Processed Runs", -90, "insideLeft", C.axisText)} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(58,73,244,0.06)" }} />
                  <Bar dataKey="processedRuns" name="Processed Runs" fill="url(#gBarRuns)" radius={[3,3,0,0]} maxBarSize={40} />
                  {compLines("runs")}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        );

      case "status":
        return (
          <>
            <div className="lc-legend">
              {[
                { color: "#198754", label: "Completed" },
                { color: "#2563eb", label: "Ready"     },
                { color: "#dc2626", label: "Failed"    },
              ].map(({ color, label }) => (
                <div className="lc-legend-item" key={label}>
                  <div className="lc-legend-swatch" style={{ background: color }} />
                  {label}
                </div>
              ))}
            </div>
            <div className="lc-chart-inner">
              {cadenceLoading ? (
                <div className="lc-loading"><div className="lc-spinner" /><span>Loading cadence data…</span></div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={cadenceChartData} margin={commonMargin}>
                    <CartesianGrid {...gridProps} />
                    <XAxis dataKey="date" {...xAxisProps} />
                    <YAxis {...yAxisProps()} label={yLabel("Runs", -90, "insideLeft")} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                    <Bar dataKey="C" name="Completed" fill="#198754" stackId="s" maxBarSize={40} />
                    <Bar dataKey="R" name="Ready"     fill="#2563eb" stackId="s" maxBarSize={40} />
                    <Bar dataKey="F" name="Failed"    fill="#dc2626" stackId="s" radius={[3,3,0,0]} maxBarSize={40} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        );

      case "size":
        return (
          <>
            {hasComp && (
              <div className="lc-legend">
                <div className="lc-legend-item">
                  <div className="lc-legend-swatch" style={{ background: C.accent2 }} />
                  Current Month
                </div>
                {compLegend}
              </div>
            )}
            <div className="lc-chart-inner">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={commonMargin}>
                  <defs>
                    <linearGradient id="gBarSize" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={C.accent2} stopOpacity={0.9} />
                      <stop offset="100%" stopColor={C.accent2} stopOpacity={0.4} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" {...xAxisProps} />
                  <YAxis {...yAxisProps()} label={yLabel("Total Size (GB)", -90, "insideLeft", C.axisText)} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(147,51,234,0.06)" }} />
                  <Bar dataKey="totalSizeGB" name="Total Size (GB)" fill="url(#gBarSize)" radius={[3,3,0,0]} maxBarSize={40} />
                  {compLines("size")}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        );

      case "average":
        return (
          <>
            <div className="lc-legend">
              <div className="lc-legend-item">
                <div className="lc-legend-swatch" style={{ background: C.teal }} />
                Avg Size / Run
              </div>
              {compLegend}
            </div>
            <div className="lc-chart-inner">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={commonMargin}>
                  <defs>
                    <linearGradient id="gTeal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.teal} stopOpacity={0.18} />
                      <stop offset="95%" stopColor={C.teal} stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="date" {...xAxisProps} />
                  <YAxis {...yAxisProps(C.teal)} label={yLabel("Avg Size (GB)", -90, "insideLeft", C.teal)} />
                  <Tooltip content={<ChartTooltip />} cursor={{ stroke: C.grid, strokeWidth: 1 }} />
                  <Area type="monotone" dataKey="averageSizeGB" name=" " fill="url(#gTeal)" stroke="none" legendType="none" />
                  <Line type="monotone" dataKey="averageSizeGB" name="Avg Size (GB)" stroke={C.teal} strokeWidth={2} dot={{ r:3, fill:C.teal, strokeWidth:0 }} activeDot={{ r:5 }} />
                  {compLines("avg")}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        );

      default: return null;
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="lc-root">

      {/* ── Left sidebar ── */}
      <div className="lc-sidebar">

        {/* Sidebar header: title + live dot */}
        <div className="lc-sidebar-header">
          <span className="lc-title-dot" style={{ marginTop: 4 }} />
          <div>
            <div className="lc-page-title">Process Run Timeline</div>
            <div className="lc-page-subtitle">{aggLabel().toLowerCase()}</div>
          </div>
        </div>

        {/* Time Range */}
        <div className="lc-sidebar-section">
          <div className="lc-sidebar-label">Time Range</div>
          <div className="lc-sidebar-btns">
            {TIME_BTNS.map(({ key, label }) => (
              <button
                key={key}
                className={`lc-sidebar-btn${timeRange === key ? " active" : ""}`}
                onClick={() => setTimeRange(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Drill Into (6m only) */}
        {timeRange === "6m" && sixMonthOptions.length > 0 && (
          <div className="lc-sidebar-section">
            <div className="lc-sidebar-label">Drill Into</div>
            <select
              className="lc-drill-select"
              value={drillMonth || ""}
              onChange={(e) => setDrillMonth(e.target.value || null)}
              style={{ width: "100%" }}
            >
              <option value="">All 6 Months</option>
              {sixMonthOptions.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            {drillMonth && (
              <button
                className="lc-drill-clear"
                style={{ width: "100%", marginTop: 4 }}
                onClick={() => setDrillMonth(null)}
              >
                ✕ Clear
              </button>
            )}
          </div>
        )}

        {/* Compare vs (currentMonth only) */}
        {timeRange === "currentMonth" && (
          <div className="lc-sidebar-section">
            <div className="lc-sidebar-label">Compare vs</div>
            <div className="lc-sidebar-pills">
              {MONTH_OPTIONS.map(({ monthsAgo, label }) => {
                const isSelected = selectedMonths.includes(monthsAgo);
                const isLoading  = compLoading[monthsAgo];
                return (
                  <button
                    key={monthsAgo}
                    className={`lc-month-pill${isSelected ? " selected" : ""}`}
                    style={isSelected ? { borderColor: COMPARE_COLORS[(monthsAgo - 1) % COMPARE_COLORS.length], color: COMPARE_COLORS[(monthsAgo - 1) % COMPARE_COLORS.length] } : {}}
                    onClick={() => toggleMonth(monthsAgo)}
                    title={label}
                  >
                    {isLoading ? "…" : label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Metadata filters */}
        <div className="lc-sidebar-section" style={{ flex: 1 }}>
          <div className="lc-sidebar-meta-head">
            <div className="lc-sidebar-label">
              <i className="bi bi-funnel-fill" />
              Metadata
              {activeMetaCount > 0 && <span className="lc-meta-badge">{activeMetaCount}</span>}
            </div>
            {activeMetaCount > 0 && (
              <button
                className="lc-meta-clear"
                style={{ fontSize: 10, padding: "2px 6px" }}
                onClick={() => setMetaFilters({ name: [], organism: [], platform: [], country: [], center: [], continent: [], isoSource: [] })}
              >
                ✕ Clear
              </button>
            )}
          </div>
          <div className="lc-sidebar-filters">
            <MetaMultiSelect label="Name"       options={metaOptions.names}            selected={metaFilters.name}      onChange={v => setMetaFilters(f => ({ ...f, name: v }))} />
            <MetaMultiSelect label="Organism"   options={metaOptions.organisms}        selected={metaFilters.organism}  onChange={v => setMetaFilters(f => ({ ...f, organism: v }))} />
            <MetaMultiSelect label="Platform"   options={metaOptions.platforms}        selected={metaFilters.platform}  onChange={v => setMetaFilters(f => ({ ...f, platform: v }))} />
            <MetaMultiSelect label="Country"    options={metaOptions.countries}        selected={metaFilters.country}   onChange={v => setMetaFilters(f => ({ ...f, country: v }))} />
            <MetaMultiSelect label="Continent"  options={metaOptions.continents}       selected={metaFilters.continent} onChange={v => setMetaFilters(f => ({ ...f, continent: v }))} />
            <MetaMultiSelect label="Center"     options={metaOptions.centers}          selected={metaFilters.center}    onChange={v => setMetaFilters(f => ({ ...f, center: v }))} />
            <MetaMultiSelect label="Iso Source" options={metaOptions.isolationSources} selected={metaFilters.isoSource} onChange={v => setMetaFilters(f => ({ ...f, isoSource: v }))} />
          </div>
        </div>

      </div>

      {/* ── Main content ── */}
      <div className="lc-main">

        {/* ── KPI strip ── */}
        <div className="lc-kpi-strip">
          {[
            { label: "Periods",        value: loading ? "—" : kpiData.periods.toLocaleString(),                                                      icon: "bi bi-calendar3",      cls: "kpi-blue"   },
            { label: "Total Runs",     value: loading ? "—" : kpiData.totalRuns.toLocaleString(),                                                    icon: "bi bi-arrow-repeat",   cls: "kpi-indigo" },
            { label: "Total Size",     value: loading ? "—" : `${kpiData.totalGB.toLocaleString()} GB`,                                              icon: "bi bi-hdd-stack",      cls: "kpi-purple" },
            { label: "Avg Size / Run", value: loading ? "—" : `${kpiData.avgGB.toLocaleString(undefined, { maximumFractionDigits: 4 })} GB`,         icon: "bi bi-bar-chart-line", cls: "kpi-teal"   },
          ].map(({ label, value, icon, cls }) => (
            <div className="lc-kpi-card" key={label}>
              <div className={`lc-kpi-icon ${cls}`}><i className={icon} /></div>
              <div className="lc-kpi-body">
                <span className="lc-kpi-label">{label}</span>
                <span className="lc-kpi-value">{value}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ── Cadence state breakdown strip ── */}
        {(() => {
          const cadenceTotal = Object.values(cadenceTotals).reduce((s, v) => s + v, 0);
          const CADENCE_CARDS = [
            { key: 'C', label: 'Completed', cls: 'status-green', icon: 'bi bi-check-circle-fill' },
            { key: 'R', label: 'Ready',     cls: 'status-blue',  icon: 'bi bi-play-circle-fill'  },
            { key: 'F', label: 'Failed',    cls: 'status-red',   icon: 'bi bi-x-circle-fill'     },
          ];
          return (
            <div className="lc-status-strip">
              {CADENCE_CARDS.map(({ key, label, cls, icon }) => (
                <div key={key} className={`lc-status-card ${cls}`}>
                  <div className="lc-status-icon"><i className={icon} /></div>
                  <div className="lc-status-body">
                    <span className="lc-status-label">{label}</span>
                    <span className="lc-status-value">
                      {cadenceLoading ? "—" : cadenceTotals[key].toLocaleString()}
                    </span>
                  </div>
                  <div className="lc-status-pct">
                    {!cadenceLoading && cadenceTotal > 0 &&
                      <span>{Math.round(cadenceTotals[key] / cadenceTotal * 100)}%</span>}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* ── Chart card ── */}
        <div className="lc-chart-card">
          <div className="lc-tabs">
            {TABS.map(({ key, label }) => (
              <button
                key={key}
                className={`lc-tab${activeTab === key ? " active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="lc-chart-area">
            {renderChart()}
          </div>
        </div>

        {/* ── Table card ── */}
        <div className="lc-table-card">
          <div className="lc-details-header">Data Details</div>
          <DataTable
            rows={combinedGrid}
            loading={displayLoading || anyCompLoading}
            rawTotalRuns={!(timeRange === "6m" && drillMonth) && !hasComp ? rawTotals.totalRuns : undefined}
            rawTotalGB={!(timeRange === "6m" && drillMonth) && !hasComp ? rawTotals.totalGB : undefined}
          />
        </div>

      </div>

    </div>
  );
}

export default Linechart;